import { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Text,
  Group,
  Stack,
  Badge,
  Box,
  Button,
  Divider,
  Tabs,
  Paper,
  Accordion,
  Loader,
  SimpleGrid,
  ThemeIcon,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import {
  User,
  Briefcase,
  FileText,
  Grid,
  CheckCircle,
  Clock,
  ExternalLink,
  Trash2,
  Edit2,
  Save,
  X,
  AlertTriangle,
  Mail,
} from 'react-feather';
import { useNavigate } from 'react-router-dom';
import type { Participant, ParticipantStatus, CohortStatus, CohortHistoryEntry, EmailHistoryEntry, AbsenceEmailHistoryEntry, ParticipantFlagWithDetails } from '../../../types';
import { formatDateTime } from '../../../utils/formatters';
import { participantsApi, type CohortCustomFieldDefinition, type UpdateParticipantRequest, type UpdateIdentityRequest } from '../../../services/api/participants';
import { intakeApi, type FormSubmission } from '../../../services/api/intake';
import { sessionsApi } from '../../../services/api/sessions';
import { cohortsApi } from '../../../services/api/cohorts';
import type { ConcernFlagWithDetails } from '../../../services/api/coachPortal';
import { ParticipantAvatar, GroupBadge, sanitizeHtml } from '../../../components/common';
import { useFeatureFlag } from '../../../stores/featureFlags';
import { usePersonaStore } from '../../../stores/persona';
import { CohortHistoryCard } from './CohortHistoryCard';
import { CohortAttendanceSection } from './CohortAttendanceSection';
import { WithdrawnPill } from './participants/ParticipantCellRenderers';
import { ParticipantNotesTab } from './participants/ParticipantNotesTab';
import { ParticipantEmailAliases } from './participants/ParticipantEmailAliases';
import classes from './ParticipantDetailModal.module.css';

interface CohortGroup {
  id: string;
  name: string;
  coach_name?: string;
}

const STATUS_CONFIG: Record<ParticipantStatus, { color: string; label: string }> = {
  pending: { color: 'warningAmber', label: 'Pending' },
  active: { color: 'medleyGreen', label: 'Active' },
  withdrawn: { color: 'gray', label: 'Withdrawn' },
};

/** Cohort statuses considered "live" (where Remove is hidden, Withdraw is available) */
const LIVE_STATUSES: CohortStatus[] = ['live', 'wrap_up'];

interface ParticipantDetailModalProps {
  opened: boolean;
  onClose: () => void;
  participant: Participant | null;
  cohortId: string;
  cohortStatus?: CohortStatus;
  groups: CohortGroup[];
  customFieldDefinitions?: CohortCustomFieldDefinition[];
  onUpdate: () => void;
  onDelete: () => void;
  onWithdraw?: () => void;
  onReactivate?: () => void;
  defaultTab?: string;
  initialEditMode?: boolean;
}

export function ParticipantDetailModal({
  opened,
  onClose,
  participant,
  cohortId,
  cohortStatus,
  groups,
  customFieldDefinitions = [],
  onUpdate,
  onDelete,
  onWithdraw,
  onReactivate,
  defaultTab,
  initialEditMode = false,
}: ParticipantDetailModalProps) {
  const navigate = useNavigate();
  const attendanceFollowUpsEnabled = useFeatureFlag('attendance_follow_ups');
  // MED-877: Internal notes tab is restricted to Medley admin + cohort manager personas.
  // Server enforces the same gate on the underlying API endpoints.
  const activePersona = usePersonaStore((state) => state.activePersona);
  const canSeeInternalNotes = activePersona === 'admin' || activePersona === 'cohort_manager';
  const [activeTab, setActiveTab] = useState<string | null>('profile');
  const [formSubmissions, setFormSubmissions] = useState<FormSubmission[]>([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [cohortHistory, setCohortHistory] = useState<CohortHistoryEntry[]>([]);
  const [loadingCohortHistory, setLoadingCohortHistory] = useState(false);
  const [emailHistory, setEmailHistory] = useState<EmailHistoryEntry[]>([]);
  const [loadingEmailHistory, setLoadingEmailHistory] = useState(false);
  const [absenceEmailHistory, setAbsenceEmailHistory] = useState<AbsenceEmailHistoryEntry[]>([]);
  const [loadingAbsenceEmailHistory, setLoadingAbsenceEmailHistory] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [concernFlags, setConcernFlags] = useState<ConcernFlagWithDetails[]>([]);
  const [participantFlags, setParticipantFlags] = useState<ParticipantFlagWithDetails[]>([]);

  // Form for core identity fields
  const identityForm = useForm<UpdateIdentityRequest>({
    initialValues: {
      first_name: '',
      last_name: '',
      email: '',
    },
  });

  // Form for cohort-specific fields
  const enrollmentForm = useForm<UpdateParticipantRequest & { custom_fields: Record<string, string> }>({
    initialValues: {
      personality_type: '',
      role: '',
      business_unit: '',
      location: '',
      manager: '',
      manager_email: '',
      level: '',
      dnm: '',
      dnm_groupings: '',
      status: 'active',
      group_id: null,
      custom_fields: {},
    },
  });

  // Reset forms when participant changes
  useEffect(() => {
    if (participant) {
      identityForm.setValues({
        first_name: participant.first_name || '',
        last_name: participant.last_name || '',
        email: participant.email || '',
      });
      enrollmentForm.setValues({
        personality_type: participant.personality_type || '',
        role: participant.role || '',
        business_unit: participant.business_unit || '',
        location: participant.location || '',
        manager: participant.manager || '',
        manager_email: participant.manager_email || '',
        level: participant.level || '',
        dnm: participant.dnm || '',
        dnm_groupings: participant.dnm_groupings || '',
        status: participant.status,
        group_id: participant.group_id,
        custom_fields: participant.custom_fields || {},
      });
    }
    setIsEditing(initialEditMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participant?.id, initialEditMode]);

  const loadFormSubmissions = useCallback(async () => {
    if (!participant) return;
    setLoadingSubmissions(true);
    try {
      const submissions = await intakeApi.getParticipantSubmissions(cohortId, participant.id);
      setFormSubmissions(submissions);
    } catch (error) {
      console.error('Failed to load form submissions:', error);
    } finally {
      setLoadingSubmissions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortId, participant?.id]);

  const loadCohortHistory = useCallback(async () => {
    if (!participant) return;
    setLoadingCohortHistory(true);
    try {
      const history = await participantsApi.getCohortHistory(cohortId, participant.id);
      setCohortHistory(history);
    } catch (error) {
      console.error('Failed to load cohort history:', error);
    } finally {
      setLoadingCohortHistory(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortId, participant?.id]);

  const loadEmailHistory = useCallback(async () => {
    if (!participant) return;
    setLoadingEmailHistory(true);
    try {
      const history = await participantsApi.getEmailHistory(cohortId, participant.id);
      setEmailHistory(history);
    } catch (error) {
      console.error('Failed to load email history:', error);
    } finally {
      setLoadingEmailHistory(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortId, participant?.id]);

  const loadAbsenceEmailHistory = useCallback(async () => {
    if (!participant) return;
    setLoadingAbsenceEmailHistory(true);
    try {
      const history = await participantsApi.getAbsenceEmailHistory(cohortId, participant.id);
      setAbsenceEmailHistory(history);
    } catch (error) {
      console.error('Failed to load absence email history:', error);
    } finally {
      setLoadingAbsenceEmailHistory(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortId, participant?.id]);

  const loadConcernFlags = useCallback(async () => {
    if (!participant) return;
    try {
      const flags = await sessionsApi.getParticipantConcernFlags(cohortId, participant.user_id);
      setConcernFlags(flags);
    } catch (error) {
      console.error('Failed to load concern flags:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortId, participant?.user_id]);

  const loadParticipantFlags = useCallback(async () => {
    if (!participant) return;
    try {
      const flags = await cohortsApi.getParticipantFlagsForParticipant(cohortId, participant.user_id);
      setParticipantFlags(flags);
    } catch (error) {
      console.error('Failed to load participant flags:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortId, participant?.user_id]);

  // Load submissions when Documents tab is activated
  useEffect(() => {
    if (opened && activeTab === 'documents' && participant && formSubmissions.length === 0) {
      loadFormSubmissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, activeTab, participant?.id, loadFormSubmissions]);

  // Load cohort history when Activity tab is activated
  useEffect(() => {
    if (opened && activeTab === 'activity' && participant && cohortHistory.length === 0) {
      loadCohortHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, activeTab, participant?.id, loadCohortHistory, cohortHistory.length]);

  // Load email history when Activity tab is activated
  useEffect(() => {
    if (opened && activeTab === 'activity' && participant && emailHistory.length === 0) {
      loadEmailHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, activeTab, participant?.id, loadEmailHistory, emailHistory.length]);

  // Load absence email history when Activity tab is activated (only when feature flag enabled)
  useEffect(() => {
    if (attendanceFollowUpsEnabled && opened && activeTab === 'activity' && participant && absenceEmailHistory.length === 0) {
      loadAbsenceEmailHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendanceFollowUpsEnabled, opened, activeTab, participant?.id, loadAbsenceEmailHistory, absenceEmailHistory.length]);

  // Load concern flags and participant flags when modal opens (needed for flag display in header/profile)
  useEffect(() => {
    if (opened && participant) {
      loadConcernFlags();
      loadParticipantFlags();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, participant?.id, loadConcernFlags, loadParticipantFlags]);

  // Reset tab when modal opens with different participant
  useEffect(() => {
    if (opened) {
      setActiveTab(defaultTab || 'profile');
      setFormSubmissions([]);
      setCohortHistory([]);
      setEmailHistory([]);
      setConcernFlags([]);
    }
  }, [participant?.id, opened]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!participant) return;

    try {
      setSaving(true);

      // Update identity (affects all cohorts)
      const identityChanged =
        identityForm.values.first_name !== participant.first_name ||
        identityForm.values.last_name !== participant.last_name ||
        identityForm.values.email !== participant.email;

      if (identityChanged) {
        await participantsApi.updateIdentity(cohortId, participant.id, identityForm.values);
      }

      // Update enrollment (affects only this cohort)
      await participantsApi.update(cohortId, participant.id, {
        personality_type: enrollmentForm.values.personality_type || undefined,
        role: enrollmentForm.values.role || undefined,
        business_unit: enrollmentForm.values.business_unit || undefined,
        location: enrollmentForm.values.location || undefined,
        manager: enrollmentForm.values.manager || undefined,
        manager_email: enrollmentForm.values.manager_email || undefined,
        level: enrollmentForm.values.level || undefined,
        dnm: enrollmentForm.values.dnm || undefined,
        dnm_groupings: enrollmentForm.values.dnm_groupings || undefined,
        custom_fields: enrollmentForm.values.custom_fields,
        status: enrollmentForm.values.status,
        group_id: enrollmentForm.values.group_id,
      });

      onUpdate();
      setIsEditing(false);
      notifications.show({
        title: 'Success',
        message: 'Participant updated successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update participant',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (participant) {
      identityForm.setValues({
        first_name: participant.first_name || '',
        last_name: participant.last_name || '',
        email: participant.email || '',
      });
      enrollmentForm.setValues({
        personality_type: participant.personality_type || '',
        role: participant.role || '',
        business_unit: participant.business_unit || '',
        location: participant.location || '',
        manager: participant.manager || '',
        manager_email: participant.manager_email || '',
        level: participant.level || '',
        dnm: participant.dnm || '',
        dnm_groupings: participant.dnm_groupings || '',
        status: participant.status,
        group_id: participant.group_id,
        custom_fields: participant.custom_fields || {},
      });
    }
    setIsEditing(false);
  };

  const handleRemoveParticipant = () => {
    if (!participant) return;

    modals.openConfirmModal({
      title: 'Remove Participant',
      children: (
        <Text size="sm">
          Are you sure you want to remove <strong>{participant.first_name} {participant.last_name}</strong> from this cohort? This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Remove', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await participantsApi.delete(cohortId, participant.id);
          notifications.show({
            title: 'Participant Removed',
            message: `${participant.first_name} ${participant.last_name} has been removed from the cohort`,
            color: 'green',
          });
          onDelete();
          onClose();
        } catch (error) {
          notifications.show({
            title: 'Error',
            message: error instanceof Error ? error.message : 'Failed to remove participant',
            color: 'red',
          });
        }
      },
    });
  };

  // Handle resolving a concern flag
  const handleResolveFlag = async (flagId: string) => {
    if (!participant) return;
    try {
      await participantsApi.resolveConcernFlag(cohortId, participant.id, flagId);
      notifications.show({
        title: 'Flag Resolved',
        message: 'Concern flag has been marked as resolved',
        color: 'green',
      });
      setConcernFlags([]);
      loadConcernFlags();
      onUpdate();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to resolve flag',
        color: 'red',
      });
    }
  };

  // Handle dismissing a concern flag
  const handleDismissFlag = async (flagId: string) => {
    if (!participant) return;
    try {
      await participantsApi.dismissConcernFlag(cohortId, participant.id, flagId);
      notifications.show({
        title: 'Flag Dismissed',
        message: 'Concern flag has been dismissed',
        color: 'green',
      });
      setConcernFlags([]);
      loadConcernFlags();
      onUpdate();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to dismiss flag',
        color: 'red',
      });
    }
  };

  // Handle dismissing a unified participant flag
  const handleDismissParticipantFlag = async (flagId: string) => {
    try {
      await cohortsApi.dismissParticipantFlag(cohortId, flagId);
      notifications.show({
        title: 'Flag Dismissed',
        message: 'Flag has been dismissed',
        color: 'green',
      });
      loadParticipantFlags();
      onUpdate();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to dismiss flag',
        color: 'red',
      });
    }
  };

  // Handle marking a unified participant flag as addressed
  const handleAddressParticipantFlag = async (flagId: string) => {
    try {
      await cohortsApi.addressParticipantFlag(cohortId, flagId);
      notifications.show({
        title: 'Flag Addressed',
        message: 'Flag has been marked as addressed',
        color: 'green',
      });
      loadParticipantFlags();
      onUpdate();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to address flag',
        color: 'red',
      });
    }
  };

  const formatFormType = (type: string) => {
    const types: Record<string, { label: string; icon: typeof FileText }> = {
      intake: { label: 'Intake Form', icon: FileText },
      session_feedback: { label: 'Post-Session Survey', icon: FileText },
      mid_program: { label: 'Midpoint Survey', icon: FileText },
      final_survey: { label: 'Endpoint Survey', icon: FileText },
      nps: { label: 'NPS Survey', icon: FileText },
      other: { label: 'Survey', icon: FileText },
    };
    return types[type] || { label: type, icon: FileText };
  };

  const formatSubmissionValue = (value: unknown): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  if (!participant) return null;

  const statusConfig = STATUS_CONFIG[participant.status];

  // Determine if we have intake data
  const hasIntakeData = participant.experience || participant.primary_goal ||
    participant.intake_timezone || participant.availability ||
    participant.direct_reports || participant.development_goal;

  // Get display values (from form when editing, participant when viewing)
  const displayFirstName = (isEditing ? identityForm.values.first_name : participant.first_name) || '';
  const displayLastName = (isEditing ? identityForm.values.last_name : participant.last_name) || '';
  const displayEmail = (isEditing ? identityForm.values.email : participant.email) || '';
  const displayGroupId = isEditing ? enrollmentForm.values.group_id : participant.group_id;
  const displayGroup = groups.find((g) => g.id === displayGroupId);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      padding={0}
      radius="md"
      withCloseButton={false}
      styles={{ body: { padding: 0 } }}
    >
      {/* Header */}
      <Box className={classes.header}>
        <Group justify="space-between" align="flex-start">
          <Group gap="sm">
            <ParticipantAvatar
              firstName={displayFirstName}
              lastName={displayLastName}
              size={56}
            />
            <Box>
              {isEditing ? (
                <Stack gap="xs">
                  <Group gap="xs">
                    <TextInput
                      size="sm"
                      placeholder="First name"
                      style={{ width: 120 }}
                      {...identityForm.getInputProps('first_name')}
                    />
                    <TextInput
                      size="sm"
                      placeholder="Last name"
                      style={{ width: 120 }}
                      {...identityForm.getInputProps('last_name')}
                    />
                  </Group>
                  <TextInput
                    size="sm"
                    placeholder="Email"
                    style={{ width: 250 }}
                    {...identityForm.getInputProps('email')}
                  />
                </Stack>
              ) : (
                <>
                  <Text fw={600} size="lg">
                    {participant.first_name} {participant.last_name}
                    {participant.preferred_name && (
                      <Text span c="dimmed" fw={400}> ({participant.preferred_name})</Text>
                    )}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {participant.email}
                  </Text>
                </>
              )}
              <Group gap="xs" mt="xs">
                <Badge
                  color={statusConfig.color}
                  variant="light"
                  size="sm"
                  tt="none"
                  leftSection={<Box component="span" style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'currentColor' }} />}
                >
                  {statusConfig.label}
                </Badge>
                <GroupBadge
                  groupName={displayGroup?.name || participant.group_name}
                  groupId={displayGroupId}
                />
                {participant.status === 'withdrawn' && (
                  <WithdrawnPill withdrawnAt={participant.withdrawn_at} />
                )}
                {participant.status !== 'withdrawn' && participant.has_open_flag && (
                  <Badge color="warningAmber" variant="light" size="sm" leftSection={<AlertTriangle size={12} />}>
                    Flagged Concern
                  </Badge>
                )}
              </Group>
            </Box>
          </Group>
          <Button
            variant="subtle"
            color="gray"
            size="compact-md"
            onClick={onClose}
            style={{ fontSize: 24, lineHeight: 1, padding: '4px 8px' }}
          >
            ×
          </Button>
        </Group>
      </Box>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List px="lg" className={classes.tabList}>
          <Tabs.Tab value="profile">Profile</Tabs.Tab>
          <Tabs.Tab value="documents">Documents</Tabs.Tab>
          <Tabs.Tab value="activity">Activity</Tabs.Tab>
          {canSeeInternalNotes && <Tabs.Tab value="notes">Notes</Tabs.Tab>}
        </Tabs.List>

        {/* Profile Tab */}
        <Tabs.Panel value="profile">
          <Box className={classes.tabContent}>
            <Stack gap="md">
              {/* FLAG CALLOUT — show active flag reason and notes prominently */}
              {(() => {
                const activeFlags = participantFlags.filter((f) => f.status === 'active');
                const openConcernFlags = concernFlags.filter((f) => f.status === 'open');
                if (activeFlags.length === 0 && openConcernFlags.length === 0) return null;

                // Prefer unified participant flags, fall back to concern flags
                const unifiedFlag = activeFlags[0];
                const concernFlag = openConcernFlags[0];
                const reason = unifiedFlag?.flag_reason || concernFlag?.notes || null;
                const flaggedBy = unifiedFlag?.source_name || concernFlag?.coach_name || null;

                return (
                  <Paper
                    p="sm"
                    withBorder
                    style={{ borderColor: '#E9A23B', backgroundColor: '#FEF3E7' }}
                  >
                    <Group gap="xs" mb={4}>
                      <AlertTriangle size={14} color="#E9A23B" />
                      <Text size="sm" fw={600}>Flagged Concern</Text>
                    </Group>
                    {flaggedBy && <Text size="xs" c="dimmed" mb={2}>Flagged by {flaggedBy}</Text>}
                    {reason && <Text size="sm">{reason}</Text>}
                  </Paper>
                );
              })()}

              {/* PROFILE DATA — persistent participant info */}
              <Box>
                <Group gap="xs" mb="xs">
                  <ThemeIcon size="sm" variant="transparent" color="gray">
                    <User size={14} />
                  </ThemeIcon>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Basic Information
                  </Text>
                </Group>
                {isEditing ? (
                  <SimpleGrid cols={2} spacing="sm" verticalSpacing="xs">
                    <Group grow gap="xs" style={{ gridColumn: 'span 2' }}>
                      <TextInput label="First Name" size="sm" {...identityForm.getInputProps('first_name')} />
                      <TextInput label="Last Name" size="sm" {...identityForm.getInputProps('last_name')} />
                    </Group>
                    <TextInput label="Email" size="sm" {...identityForm.getInputProps('email')} />
                    <TextInput label="Timezone" size="sm" value={participant.intake_timezone || ''} readOnly disabled />
                  </SimpleGrid>
                ) : (
                  <SimpleGrid cols={2} spacing="sm" verticalSpacing="xs">
                    <Box>
                      <Text size="xs" c="dimmed">Full Name</Text>
                      <Text size="sm" fw={500}>{displayFirstName} {displayLastName}</Text>
                    </Box>
                    <Box>
                      <Text size="xs" c="dimmed">Email</Text>
                      <Text size="sm" fw={500}>{displayEmail}</Text>
                    </Box>
                    <Box>
                      <Text size="xs" c="dimmed">Timezone</Text>
                      <Text size="sm" fw={500}>{participant.intake_timezone || '—'}</Text>
                    </Box>
                  </SimpleGrid>
                )}
              </Box>

              {/* OTHER EMAIL ADDRESSES (MDF-127) */}
              {/* Gated to internal staff. The API enforces the same gate, and
                  this only hides a control the server would refuse anyway. */}
              {canSeeInternalNotes && (
                <>
                  <Divider />
                  <Box>
                    <Group gap="xs" mb="xs">
                      <ThemeIcon size="sm" variant="transparent" color="gray">
                        <Mail size={14} />
                      </ThemeIcon>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                        Other Email Addresses
                      </Text>
                    </Group>
                    <ParticipantEmailAliases
                      cohortId={cohortId}
                      participantId={participant.id}
                      // Saved address, not displayEmail. displayEmail is the
                      // unsaved edit draft, and this panel badges it "Signs in".
                      primaryEmail={participant.email}
                    />
                  </Box>
                </>
              )}

              <Divider />

              {/* WORK INFORMATION */}
              <Box>
                <Group gap="xs" mb="xs">
                  <ThemeIcon size="sm" variant="transparent" color="gray">
                    <Briefcase size={14} />
                  </ThemeIcon>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Work Information
                  </Text>
                  <Badge size="xs" variant="light" color="blue">
                    From CSV
                  </Badge>
                </Group>
                {isEditing ? (
                  <SimpleGrid cols={2} spacing="sm" verticalSpacing="xs">
                    <TextInput label="Role" size="sm" placeholder="Job role" {...enrollmentForm.getInputProps('role')} />
                    <TextInput label="Business Unit" size="sm" placeholder="Business unit / opco" {...enrollmentForm.getInputProps('business_unit')} />
                    <TextInput label="Location" size="sm" placeholder="Location" {...enrollmentForm.getInputProps('location')} />
                    <TextInput label="Manager" size="sm" placeholder="Manager name" {...enrollmentForm.getInputProps('manager')} />
                    <TextInput label="Manager Email" size="sm" placeholder="Manager email address" {...enrollmentForm.getInputProps('manager_email')} />
                    <TextInput label="Level" size="sm" placeholder="Organizational level" {...enrollmentForm.getInputProps('level')} />
                    <TextInput label="Personality Type" size="sm" placeholder="e.g., INTJ, ENFP" {...enrollmentForm.getInputProps('personality_type')} />
                  </SimpleGrid>
                ) : (
                  <SimpleGrid cols={2} spacing="sm" verticalSpacing="xs">
                    <Box>
                      <Text size="xs" c="dimmed">Role</Text>
                      <Text size="sm" fw={500}>{participant.role || '—'}</Text>
                    </Box>
                    <Box>
                      <Text size="xs" c="dimmed">Business Unit</Text>
                      <Text size="sm" fw={500}>{participant.business_unit || '—'}</Text>
                    </Box>
                    <Box>
                      <Text size="xs" c="dimmed">Location</Text>
                      <Text size="sm" fw={500}>{participant.location || '—'}</Text>
                    </Box>
                    {participant.manager && (
                      <Box>
                        <Text size="xs" c="dimmed">Manager</Text>
                        <Text size="sm" fw={500}>{participant.manager}</Text>
                      </Box>
                    )}
                    {participant.manager_email && (
                      <Box>
                        <Text size="xs" c="dimmed">Manager Email</Text>
                        <Text size="sm" fw={500}>{participant.manager_email}</Text>
                      </Box>
                    )}
                    {participant.level && (
                      <Box>
                        <Text size="xs" c="dimmed">Level</Text>
                        <Text size="sm" fw={500}>{participant.level}</Text>
                      </Box>
                    )}
                    {participant.personality_type && (
                      <Box>
                        <Text size="xs" c="dimmed">Personality Type</Text>
                        <Badge size="sm" variant="light" color="blue">{participant.personality_type}</Badge>
                      </Box>
                    )}
                  </SimpleGrid>
                )}
              </Box>

              <Divider />

              {/* COHORT SPECIFIC DATA — custom fields + cohort-tied fields */}
              {(customFieldDefinitions.length > 0 || (participant.custom_fields && Object.keys(participant.custom_fields).length > 0) || participant.dnm || participant.dnm_groupings || isEditing) && (
                <>
                  <Box>
                    <Group gap="xs" mb="xs">
                      <ThemeIcon size="sm" variant="transparent" color="gray">
                        <FileText size={14} />
                      </ThemeIcon>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                        Cohort Specific Data
                      </Text>
                    </Group>
                    {isEditing ? (
                      <SimpleGrid cols={2} spacing="sm" verticalSpacing="xs">
                        <TextInput label="DNM" size="sm" placeholder="DNM designation" {...enrollmentForm.getInputProps('dnm')} />
                        <TextInput label="DNM Groupings" size="sm" placeholder="DNM groupings" {...enrollmentForm.getInputProps('dnm_groupings')} />
                        {customFieldDefinitions.map((field) => (
                          <TextInput
                            key={field.id}
                            label={field.field_label}
                            size="sm"
                            value={enrollmentForm.values.custom_fields?.[field.field_name] || ''}
                            onChange={(e) => {
                              enrollmentForm.setFieldValue('custom_fields', {
                                ...enrollmentForm.values.custom_fields,
                                [field.field_name]: e.target.value,
                              });
                            }}
                          />
                        ))}
                      </SimpleGrid>
                    ) : (
                      <SimpleGrid cols={2} spacing="sm" verticalSpacing="xs">
                        {participant.dnm && (
                          <Box>
                            <Text size="xs" c="dimmed">DNM</Text>
                            <Text size="sm" fw={500}>{participant.dnm}</Text>
                          </Box>
                        )}
                        {participant.dnm_groupings && (
                          <Box>
                            <Text size="xs" c="dimmed">DNM Groupings</Text>
                            <Text size="sm" fw={500}>{participant.dnm_groupings}</Text>
                          </Box>
                        )}
                        {Object.entries(participant.custom_fields || {}).map(([key, value]) => {
                          const fieldDef = customFieldDefinitions.find((f) => f.field_name === key);
                          return (
                            <Box key={key}>
                              <Text size="xs" c="dimmed">{fieldDef?.field_label || key}</Text>
                              <Text size="sm" fw={500}>{value || '—'}</Text>
                            </Box>
                          );
                        })}
                      </SimpleGrid>
                    )}
                  </Box>
                  <Divider />
                </>
              )}

              {/* INTAKE FORM RESPONSES — always read-only (AC5) */}
              <Box>
                <Group gap="xs" mb="xs">
                  <ThemeIcon size="sm" variant="transparent" color="gray">
                    <FileText size={14} />
                  </ThemeIcon>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Intake Form Responses
                  </Text>
                  <Badge size="xs" tt="none" styles={{ root: { backgroundColor: 'rgba(139, 92, 246, 0.1)', color: '#8B5CF6', border: 'none' } }}>
                    Paperform
                  </Badge>
                </Group>
                {hasIntakeData ? (
                  <SimpleGrid cols={2} spacing="sm" verticalSpacing="xs">
                    {participant.experience && (
                      <Box>
                        <Text size="xs" c="dimmed">Years of Leadership Experience</Text>
                        <Text size="sm" fw={500}>{participant.experience}</Text>
                      </Box>
                    )}
                    {participant.direct_reports && (
                      <Box>
                        <Text size="xs" c="dimmed">Team Size</Text>
                        <Text size="sm" fw={500}>{participant.direct_reports}</Text>
                      </Box>
                    )}
                    {participant.primary_goal && (
                      <Box>
                        <Text size="xs" c="dimmed">Value</Text>
                        <Text size="sm" fw={500}>{participant.primary_goal}</Text>
                      </Box>
                    )}
                    {participant.availability && (
                      <Box>
                        <Text size="xs" c="dimmed">Preferred Session Times</Text>
                        <Text size="sm" fw={500}>{participant.availability}</Text>
                      </Box>
                    )}
                    {participant.development_goal && (
                      <Box style={{ gridColumn: 'span 2' }}>
                        <Text size="xs" c="dimmed">Development Focus</Text>
                        <Paper withBorder p="sm" bg="gray.0" mt={4}>
                          <Text size="sm">{participant.development_goal}</Text>
                        </Paper>
                      </Box>
                    )}
                  </SimpleGrid>
                ) : (
                  <Text size="sm" c="dimmed">
                    {participant.intake_completed_at
                      ? 'No intake responses recorded'
                      : 'Intake form not yet completed'
                    }
                  </Text>
                )}
              </Box>

              <Divider />

              {/* GROUP ASSIGNMENT — always read-only in modal (AC6) */}
              <Box>
                <Group gap="xs" mb="xs">
                  <ThemeIcon size="sm" variant="transparent" color="gray">
                    <Grid size={14} />
                  </ThemeIcon>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Group Assignment
                  </Text>
                </Group>
                <SimpleGrid cols={2} spacing="sm" verticalSpacing="xs">
                  <Box>
                    <Text size="xs" c="dimmed">Current Group</Text>
                    {displayGroup ? (
                      <Text size="sm" fw={500}>{displayGroup.name}</Text>
                    ) : (
                      <Text size="sm" c="dimmed" fs="italic">Not assigned</Text>
                    )}
                  </Box>
                  <Box>
                    <Text size="xs" c="dimmed">Coach</Text>
                    {displayGroup?.coach_name ? (
                      <Text size="sm" fw={500}>{displayGroup.coach_name}</Text>
                    ) : (
                      <Text size="sm" c="dimmed" fs="italic">Not assigned</Text>
                    )}
                  </Box>
                </SimpleGrid>
              </Box>
            </Stack>
          </Box>
        </Tabs.Panel>

        {/* Documents Tab */}
        <Tabs.Panel value="documents">
          <Box className={classes.tabContent}>
            {loadingSubmissions ? (
              <Stack align="center" py="xl">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">Loading documents...</Text>
              </Stack>
            ) : formSubmissions.length === 0 ? (
              <Paper withBorder p="xl" ta="center">
                <ThemeIcon size={48} radius={48} color="gray" variant="light" mx="auto" mb="md">
                  <FileText size={24} />
                </ThemeIcon>
                <Text size="sm" c="dimmed">No documents yet</Text>
                <Text size="xs" c="dimmed" mt={4}>
                  Documents will appear here once the participant completes intake or feedback forms.
                </Text>
              </Paper>
            ) : (
              <Stack gap="md">
                {formSubmissions.map((submission) => {
                  const typeConfig = formatFormType(submission.form_type);
                  const isCompleted = submission.submitted_at != null;

                  return (
                    <Accordion key={submission.id} variant="separated" radius="md">
                      <Accordion.Item value={submission.id}>
                        <Accordion.Control>
                          <Group gap="md">
                            <ThemeIcon
                              size={40}
                              radius="md"
                              color={isCompleted ? 'medleyGreen' : 'warningAmber'}
                              variant="light"
                            >
                              <typeConfig.icon size={20} />
                            </ThemeIcon>
                            <Box style={{ flex: 1 }}>
                              <Text size="sm" fw={500}>{typeConfig.label}</Text>
                              <Text size="xs" c="dimmed">
                                Submitted {new Date(submission.submitted_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })}
                                {submission.form_type && ` · ${formatFormType(submission.form_type).label.includes('Survey') ? 'Post-session feedback' : ''}`}
                              </Text>
                            </Box>
                            <Badge
                              color={isCompleted ? 'medleyGreen' : 'warningAmber'}
                              variant="light"
                              size="sm"
                              tt="none"
                              leftSection={isCompleted ? <CheckCircle size={10} /> : <Clock size={10} />}
                              mr={10}
                            >
                              {isCompleted ? 'Completed' : 'Pending'}
                            </Badge>
                          </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Stack gap={0} pt="sm">
                            {submission.raw_payload?.map((field, index) => {
                              const value = formatSubmissionValue(field.value);
                              const isLongText = typeof field.value === 'string' && field.value.length > 100;

                              return (
                                <Box key={`${field.key}-${index}`}>
                                  {index > 0 && <Divider my="sm" />}
                                  <Text fz={12} c="dimmed">{field.title || field.key}</Text>
                                  {isLongText ? (
                                    <Box mt={4} style={{ background: '#FAFAF8', borderRadius: 6, padding: 12 }}>
                                      <Text fz={14}>{value}</Text>
                                    </Box>
                                  ) : (
                                    <Text fz={14} fw={500}>{value}</Text>
                                  )}
                                </Box>
                              );
                            })}
                            {submission.paperform_form_id && (
                              <Box mt="sm">
                                <Button
                                  variant="outline"
                                  size="xs"
                                  leftSection={<ExternalLink size={14} />}
                                  component="a"
                                  href={`https://paperform.co/submissions/${submission.paperform_form_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View in Paperform
                                </Button>
                              </Box>
                            )}
                          </Stack>
                        </Accordion.Panel>
                      </Accordion.Item>
                    </Accordion>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Tabs.Panel>

        {/* Activity Tab */}
        <Tabs.Panel value="activity">
          <Box className={classes.tabContent}>
            {/* FLAGGED CONCERN CALLOUTS (open flags with action buttons) */}
            {concernFlags.filter((f) => f.status === 'open').length > 0 && (
              <Stack gap="sm" mb="md">
                {concernFlags.filter((f) => f.status === 'open').map((flag) => (
                  <Paper
                    key={`callout-${flag.id}`}
                    p="md"
                    withBorder
                    style={{ borderColor: '#E9A23B', backgroundColor: '#FEF3E7' }}
                  >
                    <Group justify="space-between" mb="xs">
                      <Group gap="xs">
                        <AlertTriangle size={16} color="#E9A23B" />
                        <Text size="sm" fw={600}>Flagged Concern</Text>
                      </Group>
                      <Text size="xs" c="dimmed">
                        S{flag.session_number || '—'} &bull;{' '}
                        {new Date(flag.session_date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </Text>
                    </Group>
                    <Text size="sm" c="dimmed" mb={4}>
                      Flagged by {flag.coach_name}
                    </Text>
                    {flag.notes && (
                      <Text size="sm" mb="sm">
                        {flag.notes}
                      </Text>
                    )}
                    <Group gap="sm">
                      <Button variant="subtle" size="xs" onClick={() => handleDismissFlag(flag.id)}>
                        Dismiss
                      </Button>
                      <Button color="medleyGreen" size="xs" onClick={() => handleResolveFlag(flag.id)}>
                        Mark as Resolved
                      </Button>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            )}

            {/* UNIFIED FLAG CALLOUTS (from participant_flags - system + coach flags) */}
            {participantFlags.filter((f) => f.status === 'active').length > 0 && (
              <Stack gap="sm" mb="md">
                {participantFlags.filter((f) => f.status === 'active').map((flag) => (
                  <Paper
                    key={`unified-${flag.id}`}
                    p="md"
                    withBorder
                    style={{ borderColor: '#E9A23B', backgroundColor: '#FEF3E7' }}
                  >
                    <Group justify="space-between" mb="xs">
                      <Group gap="xs">
                        <AlertTriangle size={16} color="#E9A23B" />
                        <Text size="sm" fw={600}>Flagged Concern</Text>
                      </Group>
                      {flag.session_number && (
                        <Text size="xs" c="dimmed">
                          S{flag.session_number} &bull;{' '}
                          {flag.session_date
                            ? new Date(flag.session_date).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                              })
                            : ''}
                        </Text>
                      )}
                    </Group>
                    <Text size="sm" c="dimmed" mb={4}>
                      Flagged by {flag.source_name}
                    </Text>
                    <Text size="sm" mb="sm">
                      {flag.flag_reason}
                    </Text>
                    <Group gap="sm">
                      <Button
                        variant="subtle"
                        size="xs"
                        onClick={() => handleDismissParticipantFlag(flag.id)}
                      >
                        Dismiss
                      </Button>
                      <Button
                        color="medleyGreen"
                        size="xs"
                        onClick={() => handleAddressParticipantFlag(flag.id)}
                      >
                        Mark as Addressed
                      </Button>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            )}

            {/* Cohort Attendance Section */}
            {participant && (
              <CohortAttendanceSection
                cohortId={cohortId}
                participantId={participant.id}
                isActive={opened && activeTab === 'activity'}
              />
            )}

            {/* Absence Email History Section (E19-4: MED-500) */}
            {attendanceFollowUpsEnabled && (
              <>
                <Text
                  size="xs"
                  fw={600}
                  c="dimmed"
                  mb="md"
                  style={{ letterSpacing: '0.5px', textTransform: 'uppercase' }}
                >
                  Absence Emails
                </Text>

                {loadingAbsenceEmailHistory ? (
                  <Box ta="center" py="xl">
                    <Loader size="sm" />
                  </Box>
                ) : absenceEmailHistory.length === 0 ? (
                  <Text size="sm" c="dimmed" mb="xl">
                    No absence emails sent
                  </Text>
                ) : (
                  <Accordion variant="separated" radius="md" mb="xl">
                    {absenceEmailHistory.map((entry) => (
                  <Accordion.Item key={entry.id} value={entry.id}>
                    <Accordion.Control>
                      <Group gap="sm" wrap="nowrap">
                        <ThemeIcon size={28} radius="xl" color="dangerRed" variant="light">
                          <Mail size={14} />
                        </ThemeIcon>
                        <Box style={{ flex: 1 }}>
                          <Text size="sm" fw={500} lineClamp={1}>
                            Wk {entry.session_number}: {entry.template_name || 'Absence Email'}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {entry.sent_at ? formatDateTime(entry.sent_at) : 'Not sent'}
                            {entry.sent_by_name ? ` by ${entry.sent_by_name}` : ''}
                          </Text>
                        </Box>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="xs">
                        <Box>
                          <Text size="xs" fw={600} c="dimmed">Subject</Text>
                          <Text size="sm">{entry.rendered_subject || '(no subject)'}</Text>
                        </Box>
                        <Divider />
                        <Box>
                          <Text size="xs" fw={600} c="dimmed" mb={4}>Body</Text>
                          <Box
                            style={{
                              fontSize: 'var(--mantine-font-size-sm)',
                              lineHeight: 1.6,
                            }}
                            dangerouslySetInnerHTML={{
                              __html: sanitizeHtml(entry.rendered_body || '(no body)'),
                            }}
                          />
                        </Box>
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
                )}
              </>
            )}

            {/* Email History Section */}
            <Text
              size="xs"
              fw={600}
              c="dimmed"
              mb="md"
              style={{ letterSpacing: '0.5px', textTransform: 'uppercase' }}
            >
              Email History
            </Text>

            {loadingEmailHistory ? (
              <Box ta="center" py="xl">
                <Loader size="sm" />
              </Box>
            ) : emailHistory.length === 0 ? (
              <Text size="sm" c="dimmed" mb="xl">
                No emails sent
              </Text>
            ) : (
              <Stack gap="xs" mb="xl">
                {emailHistory.map((entry) => (
                  <Group key={entry.id} gap="sm" py={4}>
                    <ThemeIcon size={28} radius="xl" color="gray" variant="light">
                      <Mail size={14} />
                    </ThemeIcon>
                    <Box style={{ flex: 1 }}>
                      <Text size="sm" fw={500} lineClamp={1}>
                        {entry.subject}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {formatDateTime(entry.sent_at || entry.created_at)}
                      </Text>
                    </Box>
                  </Group>
                ))}
              </Stack>
            )}

            {/* Cohort History Section */}
            <Text
              size="xs"
              fw={600}
              c="dimmed"
              mb="md"
              style={{ letterSpacing: '0.5px', textTransform: 'uppercase' }}
            >
              Cohort History
            </Text>

            {loadingCohortHistory ? (
              <Box ta="center" py="xl">
                <Loader size="sm" />
              </Box>
            ) : cohortHistory.length === 0 ? (
              <Paper withBorder p="xl" ta="center">
                <ThemeIcon size={48} radius={48} color="gray" variant="light" mx="auto" mb="md">
                  <Clock size={24} />
                </ThemeIcon>
                <Text size="sm" c="dimmed">No cohort history</Text>
                <Text size="xs" c="dimmed" mt={4}>
                  This participant has not been enrolled in any cohorts.
                </Text>
              </Paper>
            ) : (
              <Stack gap="md">
                {cohortHistory.map((entry) => (
                  <CohortHistoryCard
                    key={entry.cohort_id}
                    entry={entry}
                    onClick={() => {
                      onClose();
                      navigate(`/cohorts/${entry.cohort_id}`);
                    }}
                  />
                ))}
              </Stack>
            )}
          </Box>
        </Tabs.Panel>

        {/* MED-877: Notes Tab — admin + cohort_manager only */}
        {canSeeInternalNotes && (
          <Tabs.Panel value="notes">
            <Box className={classes.tabContent}>
              <ParticipantNotesTab cohortId={cohortId} participantId={participant.id} />
            </Box>
          </Tabs.Panel>
        )}
      </Tabs>

      {/* Footer */}
      <Box className={classes.footer}>
        {isEditing ? (
          <Group justify="flex-end">
            <Button
              variant="default"
              leftSection={<X size={16} />}
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              color="medleyGreen"
              leftSection={<Save size={16} />}
              onClick={handleSave}
              loading={saving}
            >
              Save Changes
            </Button>
          </Group>
        ) : participant?.status === 'withdrawn' ? (
          <Group justify="space-between">
            {onReactivate && (
              <Button
                color="medleyGreen"
                onClick={() => { onReactivate(); onClose(); }}
              >
                Re-activate
              </Button>
            )}
            <Button variant="default" onClick={onClose}>
              Close
            </Button>
          </Group>
        ) : (
          <Group justify="space-between">
            <Group gap="sm">
              {(!cohortStatus || !LIVE_STATUSES.includes(cohortStatus)) && (
                <Button
                  variant="outline"
                  color="red"
                  leftSection={<Trash2 size={16} />}
                  onClick={handleRemoveParticipant}
                >
                  Remove Participant
                </Button>
              )}
              {cohortStatus && LIVE_STATUSES.includes(cohortStatus) && onWithdraw && (
                <Button
                  variant="outline"
                  color="orange"
                  onClick={() => { onWithdraw(); onClose(); }}
                >
                  Withdraw
                </Button>
              )}
            </Group>
            <Button
              variant="default"
              leftSection={<Edit2 size={16} />}
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
          </Group>
        )}
      </Box>
    </Modal>
  );
}

export default ParticipantDetailModal;
